/** ReportsCockpit — Cockpit frame around the Reports body. */
import { useMemo } from "react";
import { CockpitPage, CockpitMasthead, HeroMetric, HeroDivider, CockpitKpiStrip } from "@/components/cockpit/CockpitShell";
import { useAppData } from "@/data/AppDataContext";
import { getStageConfig } from "@/data/workflowEngine";
import { REPORTS_SERVER_PAGING_ENABLED } from "@/lib/dataRuntimeFlags";
import { isReportsServerPagedRoute, useBrowserLocationSnapshot } from "@/lib/routeScope";
import { useReportsServerData } from "@/hooks/useReportsServerData";
import Reports from "./Reports";

export default function ReportsCockpit() {
  const appData = useAppData();
  const location = useBrowserLocationSnapshot();
  const reportsServerEnabled = REPORTS_SERVER_PAGING_ENABLED && isReportsServerPagedRoute(location.pathname);
  const serverData = useReportsServerData({ enabled: reportsServerEnabled });
  const jobs = reportsServerEnabled ? serverData.jobs : appData.jobs;
  const issues = reportsServerEnabled ? serverData.issues : appData.issues;

  const k = useMemo(() => {
    const isCompleted = (j: typeof jobs[0]) => (getStageConfig(j.currentStage)?.order ?? 0) >= 100;
    const open = jobs.filter((j) => !isCompleted(j));
    const completed = jobs.filter(isCompleted);
    const pipeline = open.reduce((s, j) => s + (j.financials?.contractValue ?? 0), 0);
    const completedValue = completed.reduce((s, j) => s + (j.financials?.contractValue ?? 0), 0);
    const openIssues = issues.filter((i) => i.status === "Open" || i.status === "In Progress");
    const urgent = openIssues.filter((i) => i.severity === "Urgent");
    return {
      pipeline,
      completedValue,
      openJobs: open.length,
      completedJobs: completed.length,
      openIssues: openIssues.length,
      urgent: urgent.length,
      total: jobs.length,
    };
  }, [jobs, issues]);

  const fmt = (n: number) =>
    n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(1)}M` : `$${Math.round(n / 1000)}K`;

  return (
    <CockpitPage>
      <CockpitMasthead
        eyebrow={`Reporting · Operational Intelligence · ${k.total} jobs · ${fmt(k.pipeline)} active`}
        title="Reports"
        right={
          <>
            <HeroMetric label="Active Pipeline" value={fmt(k.pipeline)} hero />
            <HeroDivider />
            <HeroMetric label="Completed" value={fmt(k.completedValue)} tone="good" />
            <HeroDivider />
            <HeroMetric label="Active Jobs" value={k.openJobs} />
          </>
        }
      />
      <CockpitKpiStrip
        items={[
          { label: "Open Jobs", value: k.openJobs },
          { label: "Completed", value: k.completedJobs, tone: "good" },
          { label: "Open Issues", value: k.openIssues, tone: k.openIssues > 5 ? "warn" : "default" },
          { label: "Urgent", value: k.urgent, tone: k.urgent > 0 ? "warn" : "default" },
        ]}
      />
      <div className="cockpit-surface">
        <Reports hideHeader serverDataOverride={reportsServerEnabled ? serverData : undefined} />
      </div>
    </CockpitPage>
  );
}
